import {
  IPharmacyDetails,
  ProfessionalReplacementTypes,
  adminActions,
  convertToDecimals,
  pharmacyDetails,
  resetStatus,
  submitPharmacyDetails,
  successSelector,
} from '@pharmaplan/common';
import { FormikValues, useFormik } from 'formik';
import React, { useEffect } from 'react';
import { DateTime } from 'luxon';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import {
  IFromScreen,
  renderScreen,
  resetDrawer,
  setBack,
  setFooter,
  setFooterShow,
} from '../../../actions/drawerActions';
import strings from '../../../localization';
import OutcomeModal from '../../../components/Modals/OutcomeModal';
import {
  MomentTimeFormats,
  OutcomeModalTypes,
  ScreenTypes,
  SecondaryAdminNavigation,
} from '../../../helpers/Constants';
import { setDialog } from '../../../reducers/dialogReducer';
import pharmacyEditValidator from '../../../components/Admin/Profile/Pharmacy/PharmacyEditProfile/Validator';
import {
  rateLongTermParamsValidation,
  rateValidation,
} from '../../../helpers/Functions';
import useSelfService from '../../useSelfService';

const successAction = adminActions.submitPharmacyDetails;

const usePharmacyEditProfile = (
  fromScreen?: IFromScreen,
  hasOwner?: boolean,
  back?: () => void,
) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const details = useAppSelector(pharmacyDetails);
  const success = useAppSelector((state) =>
    successSelector([successAction], state));

  const { pharmacy } = details ?? {};
  const {
    hourlyRate,
    hourlyRateTech,
    pharmacyId,
    pharmacyHourlyRate,
    professionalWeekDayHourlyRate,
    professionalWeekendHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    entitlement,
    portedFromPG = false,
  } = pharmacy ?? {};
  const isCrossPlatformProfile = !isSelfService
    && portedFromPG;

  const { validUntil, totalCredits, promoHourlyRate } = entitlement ?? {};

  const showSavedSuccessfully = () => {
    dispatch(resetDrawer());
    dispatch(
      setDialog({
        Component: (
          <OutcomeModal
            type={OutcomeModalTypes.success}
            message={strings.profileSaved}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  const handleOnSubmit = (values: FormikValues) => {
    const submissionValues = { ...values };
    delete submissionValues.portedToPP;
    delete submissionValues.portedFromPG;
    delete submissionValues.canSendInvite;
    delete submissionValues.canResumePorting;
    delete submissionValues.lastPPInviteSentAt;

    const {
      hourlyRate: formikHourlyRate,
      hourlyRateTech: formikHourlyRateTech,
      promoHourlyRate: formikPromoHourlyRate,
      totalCredits: formikTotalCredits,
      validUntil: formikValidUntil,
    } = values ?? {};

    let formikEntitlement = null;

    if (entitlement) {
      formikEntitlement = {
        ...entitlement,
        validUntil: formikValidUntil?.toFormat(MomentTimeFormats.fullDate),
        promoHourlyRate: parseFloat(formikPromoHourlyRate),
        totalCredits: formikTotalCredits,
      };
    }

    dispatch(
      submitPharmacyDetails({
        ...submissionValues,
        ...(isCrossPlatformProfile
          ? {
            syncTechnicianShifts: false,
            syncPharmacistShifts: false,
          }
          : {}),
        hourlyRate: parseFloat(formikHourlyRate),
        hourlyRateTech: parseFloat(formikHourlyRateTech || 0),
        entitlement: formikEntitlement,
      } as IPharmacyDetails),
    );
  };

  const onSubmit = (values: FormikValues) => {
    const {
      hourlyRate: formikHourlyRate,
      pharmacyHourlyRate: formikPharmacyHourlyRate,
    } = values ?? {};

    const isPharmacyGreaterPharmacist = rateValidation({
      pharmacistRate: formikHourlyRate,
      pharmacyRate: formikPharmacyHourlyRate,
      dispatch,
      isSelfService,
      replacementType: ProfessionalReplacementTypes.pharmacist,
    });

    const isPharmacyLongParamsGreater = rateLongTermParamsValidation({
      weekdayHours: 1,
      weekendHours: 1,
      professionalWeekday: values?.professionalWeekDayHourlyRate,
      professionalWeekend: values?.professionalWeekendHourlyRate,
      pharmacyWeekday: values?.pharmacyWeekDayHourlyRate,
      pharmacyWeekend: values?.pharmacyWeekendHourlyRate,
      dispatch,
      isSelfService,
      replacementType: ProfessionalReplacementTypes.pharmacist,
    });

    if (isPharmacyGreaterPharmacist && isPharmacyLongParamsGreater) {
      handleOnSubmit(values);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...pharmacy,
      totalCredits,
      promoHourlyRate: convertToDecimals(promoHourlyRate),
      validUntil: validUntil ? DateTime.fromISO(validUntil, { zone: 'utc' }) : null,
      hourlyRate: convertToDecimals(hourlyRate),
      hourlyRateTech: convertToDecimals(hourlyRateTech),
      pharmacyHourlyRate: convertToDecimals(pharmacyHourlyRate),
      professionalWeekDayHourlyRate: convertToDecimals(
        professionalWeekDayHourlyRate,
      ),
      professionalWeekendHourlyRate: convertToDecimals(
        professionalWeekendHourlyRate,
      ),
      pharmacyWeekDayHourlyRate: convertToDecimals(pharmacyWeekDayHourlyRate),
      pharmacyWeekendHourlyRate: convertToDecimals(pharmacyWeekendHourlyRate),
      syncTechnicianShifts: isCrossPlatformProfile
        ? false
        : pharmacy?.syncTechnicianShifts ?? true,
      syncPharmacistShifts: isCrossPlatformProfile
        ? false
        : pharmacy?.syncPharmacistShifts ?? true,
    },
    enableReinitialize: true,
    onSubmit,
    validationSchema: pharmacyEditValidator,
  });

  const { handleSubmit } = formik ?? {};

  const gotoPharmacyProfile = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.profile,
        pharmacyId,
        fromScreen,
        hasOwner,
      }),
    );
  };

  useEffect(() => {
    dispatch(
      setFooter({
        show: true,
        pagination: {
          show: false,
        },
        buttons: {
          show: true,
          primaryButton: {
            label: strings.saveChanges,
            onClick: handleSubmit,
          },
        },
      }),
    );

    if (fromScreen !== SecondaryAdminNavigation.pharmacyList) {
      dispatch(setBack(back ?? gotoPharmacyProfile));
    }

    return () => {
      dispatch(setFooterShow(false));
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      showSavedSuccessfully();
      dispatch(resetStatus([successAction]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return { formik, isCrossPlatformProfile };
};

export default usePharmacyEditProfile;
